import React from 'react';
import axios from 'axios';
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, ArrowUpRight, Eye, EyeOff } from 'lucide-react';
import CONFIG from '../../config';
import { useAuth } from '../../context/AuthProvider';
import { usePatient } from '../../context/PatientContext';
import { useTheme } from '../../context/ThemeContext';
import loginImg from '../../assets/Image/LoginPageImage.jpg';

const CheckForm = () => {
  const { theme, isDarkMode, toggleTheme } = useTheme();
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const baseURL = CONFIG.BASE_URL;
  const { setDoctorInfo, setToken } = usePatient();
  const [showPassword, setShowPassword] = React.useState(false);
  const [loginMethod, setLoginMethod] = React.useState('password'); // 'password' or 'otp'
  const [otpRequested, setOtpRequested] = React.useState(false);
  const [isRequestingOtp, setIsRequestingOtp] = React.useState(false);

  const initialValues = { mobileNumber: '', password: '', otp: '' };

  const validationSchema = Yup.object().shape({
    mobileNumber: Yup.string()
      .required('Required')
      .matches(/^(\+\d{1,3}[- ]?)?\d{10}$/, 'Invalid number'),
    password: Yup.string().when('loginMethod', {
      is: 'password',
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.notRequired()
    }),
    otp: Yup.string().when('loginMethod', {
      is: 'otp',
      then: (schema) => schema.required('Required').length(6, 'Must be 6 digits'),
      otherwise: (schema) => schema.notRequired()
    })
  });

  const handleRequestOTP = async (mobileNumber) => {
    if (!mobileNumber || !/^(\+\d{1,3}[- ]?)?\d{10}$/.test(mobileNumber)) {
      alert('Please enter a valid mobile number first');
      return;
    }

    setIsRequestingOtp(true);
    try {
      // Placeholder for OTP request API
      await axios.post(`${baseURL}/mandali/request-otp`, { mobileNumber });
      // alert('OTP request sent to ' + mobileNumber + " (Demo Implementation)");
      setOtpRequested(true);
    } catch (error) {
      alert('Error requesting OTP: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsRequestingOtp(false);
    }
  };

  const handleSubmit = async (values, actions) => {
    try {
      let endpoint = loginMethod === 'password' ? '/mandali/check' : '/mandali/verify-otp';
      const { data } = await axios.post(`${baseURL}${endpoint}`, values);
      if (data.token) {
        localStorage.setItem('authToken', data.token);
        setToken(data.token);
        setAuth(data.userData);
        setDoctorInfo({
          MANDALI_ID: data.userData.MANDALI_ID,
          MANDALI_ROLE: data.userData.MANDALI_ROLE,
          CLINIC_ID: data.userData.CLINIC_LIST[0]?.CLINIC_ID || null,
          CLINIC_LIST: data.userData.CLINIC_LIST || []
        });
        if (data?.userData?.MANDALI_ROLE !== 3) {
          navigate('/patientlist');
        } else {
          navigate('/calendar');
        }
      }
    } catch (error) {
      alert('Login error: ' + (error.response?.data?.message || error.message));
    }
    actions.setSubmitting(false);
  };

  return (
    <div
      className="h-screen w-screen overflow-hidden flex items-center justify-center p-4 md:p-12 transition-colors duration-700 ease-in-out relative bg-gradient-to-t from-lime-50 via-ayurmax-primary to-ayurmax-dark"
    >
      {/* GLOBAL CSS ANIMATION */}
      <style>
        {`
          @keyframes gradientFlow {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          .animate-flow {
            background-size: 300% auto; /* Increased to 300% for longer horizontal flow */
            animation: gradientFlow 5s linear infinite;
          }
          
          @keyframes lineExpand {
            0% { width: 0%; }
            100% { width: 3rem; }
          }
          .animate-line {
            animation: lineExpand 0.8s ease-out forwards;
            animation-delay: 0.6s;
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.4s ease-out forwards;
          }
        `}
      </style>

      {/* THEME TOGGLE */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={toggleTheme}
        className="absolute top-8 right-8 z-50 p-3 rounded-full shadow-2xl backdrop-blur-md border border-white/20 transition-all bg-white/10 text-white hover:bg-white/20"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={isDarkMode ? "dark" : "light"}
            initial={{ y: -20, opacity: 0, rotate: -45 }}
            animate={{ y: 0, opacity: 1, rotate: 0 }}
            exit={{ y: 20, opacity: 0, rotate: 45 }}
            transition={{ duration: 0.2 }}
          >
            {isDarkMode ? <Sun size={24} /> : <Moon size={24} />}
          </motion.div>
        </AnimatePresence>
      </motion.button>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        style={{ backgroundColor: theme.card }}
        className={`relative w-full max-w-7xl h-full max-h-[800px] rounded-[3rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] overflow-hidden flex backdrop-blur-sm`}
      >

        {/* LEFT PANEL */}
        <div className="relative hidden lg:flex flex-[1.3] overflow-hidden bg-black">
          <motion.img
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5 }}
            src={loginImg}
            className="absolute inset-0 w-full h-full object-cover opacity-90"
            alt="Amrutdhara Wellness"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ayurmax-dark/90 via-ayurmax-primary/20 to-transparent" />

          <div className="relative h-full p-16 flex flex-col justify-between z-10 text-white w-full">
            {/* <div className="text-xl font-bold tracking-[0.3em] uppercase opacity-80">Amrutdhara</div> */}

            <div className="space-y-4 mb-30">
              <h1 className="text-7xl xl:text-8xl font-light leading-[0.9] tracking-tighter" style={{ fontFamily: theme.fonts?.serif }}>
                Discover Your <br />

                <span
                  className="italic font-serif animate-flow bg-gradient-to-r from-yellow-300 to-green-300 bg-clip-text text-transparent inline-block"
                >
                  Inner
                </span> <br />

                Balance
              </h1>
              <p className="text-white/80 max-w-sm text-lg font-medium leading-relaxed">
                Seamless digital integration for the modern Ayurvedic practitioner.
              </p>
            </div>
            <div></div>
          </div>
        </div>

        <div className={`flex-1 flex flex-col justify-center px-10 sm:px-16 md:px-24 relative transition-colors duration-500 `} style={{ backgroundColor: theme.card }}>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="w-full max-w-lg mx-auto"
          >
            <div className="mb-14">
              <div className="flex justify-between items-end">
                <div>
                  <h2 className={`text-5xl mb-3 tracking-tight font-serif`} style={{ fontFamily: theme.fonts?.serif, color: theme.text }}>
                    {loginMethod === 'password' ? 'Login' : 'OTP Login'}
                  </h2>
                  <div className={`h-1 w-0 rounded-full animate-line`} style={{ backgroundColor: theme.primary }} />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setLoginMethod(loginMethod === 'password' ? 'otp' : 'password');
                    setOtpRequested(false);
                  }}
                  className={`text-sm font-semibold transition-all hover:opacity-70`}
                  style={{ color: theme.primary }}
                >
                  {loginMethod === 'password' ? 'Login with OTP' : 'Login with Password'}
                </button>
              </div>
            </div>

            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
              {({ errors, touched, isSubmitting, values }) => (
                <Form className="space-y-10">
                  <div className="relative group">
                    <label className={`text-sm font-medium mb-2 block`} style={{ color: theme.text }}>Mobile number</label>
                    <div className="relative">
                      <Field
                        name="mobileNumber"
                        type="text"
                        placeholder="Enter mobile number"
                        className={`w-full bg-transparent border-b-2 py-4 outline-none transition-all text-xl font-medium`}
                        style={{ color: theme.text }}
                      />
                      {loginMethod === 'otp' && (
                        <button
                          type="button"
                          disabled={isRequestingOtp}
                          onClick={() => handleRequestOTP(values.mobileNumber)}
                          className="absolute right-0 top-1/2 -translate-y-1/2 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full transition-all hover:scale-105 active:scale-95 disabled:opacity-50 border border-ayurmax-primary text-ayurmax-primary hover:bg-ayurmax-primary hover:text-white"
                        >
                          {isRequestingOtp ? 'Sending...' : otpRequested ? 'Resend OTP' : 'Request OTP'}
                        </button>
                      )}
                    </div>
                    <ErrorMessage name="mobileNumber" component="div" className="absolute text-red-500 text-[10px] font-bold mt-2" />
                  </div>

                  {loginMethod === 'password' ? (
                    <div className="relative group">
                      <label className={`text-sm font-medium mb-2 block`} style={{ color: theme.text }}>Password</label>
                      <div className="relative">
                        <Field
                          name="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          className={`w-full bg-transparent border-b-2 py-4 pr-12 outline-none transition-all text-xl font-medium`}
                          style={{ color: theme.text }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className={`absolute right-0 top-1/2 -translate-y-1/2 p-2 transition-all hover:scale-110`}
                          style={{ color: theme.primary }}
                        >
                          {showPassword ? <EyeOff size={24} /> : <Eye size={24} />}
                        </button>
                      </div>
                      <ErrorMessage name="password" component="div" className="absolute text-red-500 text-[10px] font-bold mt-2" />
                    </div>
                  ) : (
                    <div className="relative group animate-fadeIn">
                      <label className={`text-sm font-medium mb-2 block`} style={{ color: theme.text }}>Enter 6-digit OTP</label>
                      <Field
                        name="otp"
                        type="text"
                        maxLength="6"
                        placeholder="000000"
                        className={`w-full bg-transparent border-b-2 py-4 outline-none transition-all text-xl font-medium tracking-[0.5em]`}
                        style={{ color: theme.text }}
                      />
                      <ErrorMessage name="otp" component="div" className="absolute text-red-500 text-[10px] font-bold mt-2" />
                    </div>
                  )}

                  {/* FIXED BUTTON - LEFT ALIGNED WITH PROPER WIDTH */}
                  <div className="pt-8">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={isSubmitting || (loginMethod === 'otp' && !otpRequested)}

                      className="animate-flow w-auto inline-flex items-center gap-3 py-5 px-12 rounded-full text-white font-bold text-2xl shadow-2xl transition-all group disabled:opacity-50 disabled:cursor-not-allowed bg-ayurmax-primary hover:bg-ayurmax-dark"
                    >
                      <span>{isSubmitting ? 'Authenticating...' : loginMethod === 'password' ? 'Sign In' : 'Verify & Login'}</span>
                      <ArrowUpRight
                        size={28}
                        strokeWidth={2.5}
                        className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                      />
                    </motion.button>
                  </div>
                </Form>
              )}
            </Formik>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default CheckForm;